// src/pages/company-head/CompanyHeadDashboard.js
import React, { useContext, useEffect, useState, useMemo } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { 
  FaHome, FaUsers, FaBuilding, FaCalendar, 
  FaClock, FaMoneyBill, FaShieldAlt, FaBox, FaUserTag,
  FaFileInvoiceDollar, FaHistory, FaBook, FaList, FaSlidersH,
  FaEye, FaShoppingCart, FaClipboardList, FaFileAlt,
  FaReceipt, FaTruck, FaChartBar, FaMobileAlt, FaUserFriends, FaChartLine,
  FaWhatsapp, FaCubes, FaUndo
} from 'react-icons/fa';
import { AuthContext } from '../../components/AuthContext';
import Dashboard from './Dashboard';
import EmployeesManagement from './EmployeesManagement';
import ShowroomAnalytics from './ShowroomAnalytics'; // New component
import BrandManagement from './BrandManagement';
import RoleManagement from './RoleManagement'; // New component
import AppSliderManagement from './AppSliderManagement'; // New component
import AppAccessLog from './AppAccessLog'; // New component
import DealerInterestLog from './DealerInterestLog'; // App dealer interest reports
import OrderManagement from './OrderManagement'; // New component
import AppTilesDealerManagement from './AppTilesDealerManagement'; // App dealer management
import AppTilesDealerAccessLog from './AppTilesDealerAccessLog'; // App dealer access logs
import TilesManagement from './TilesManagement'; // New tiles management for company head
import StockItemsManagement from './StockItemsManagement';
import axios from 'axios';
import TallyManagement from './TallyManagement';
import TilesDayBook from './reports/TilesDayBook';
import TilesLedger from './reports/TilesLedger';
import DeliveryOrdersReports from './reports/DeliveryOrderReport';
import ActivityLogsReports from './reports/ActivityLogs';
import ChoiceEntryReports from './reports/ChoiceEntryReports';
import QuotationsReports from './reports/QuotationsReports';
import ProformaInvoiceReports from './reports/ProformaInvoiceReports';
import DeliveryOrdersDetailedReports from './reports/DeliveryOrdersDetailedReports';
import GoodsReturnReports from './reports/GoodsReturnReports';
import TallyReports from './reports/TallyReports';
import WhatsAppAutomations from './WhatsAppAutomations'; // WhatsApp automation management

// Icon mapping for company head features
const featureIcons = {
  'dashboard': FaHome,
  'employee-management': FaUsers,
  'brand-management': FaBuilding,
  'role-management': FaUserTag,  // Added new icon
  'app-slider-management': FaSlidersH,  // Added new icon
  'app-access-log': FaEye,  // Added new icon
  'order-management': FaShoppingCart,  // Added new icon
  'app-dealer-management': FaMobileAlt,  // App dealer management
  'app-dealer-access-logs': FaUserFriends,  // App dealer access logs
  'app-dealer-interest-reports': FaChartLine,  // App dealer interest reports
  'tiles-management': FaBox,  // New tiles management
  'stock-items-management': FaCubes,
  'leave-management': FaCalendar,
  'asset-management': FaShieldAlt,
  'attendance-management': FaClock,
  'payroll-management': FaMoneyBill,
  'tally-management': FaBox,
  "delivery-orders-reports": FaFileInvoiceDollar,
  'showroom-analytics': FaChartBar,
  "activity-logs-reports": FaHistory,
  "tiles-day-book": FaBook,
  "tiles-ledger": FaList,
  "choice-entry-reports": FaClipboardList,
  "quotations-reports": FaFileAlt,
  "proforma-invoice-reports": FaReceipt,
  "delivery-orders-detailed-reports": FaTruck,
  'goods-return-reports': FaUndo,
  'tally-reports': FaBook,
  'whatsapp-automations': FaWhatsapp
};

// Helper function to find matching domain config
const findMatchingDomainConfig = (domains = [], hostname) => {
  if (!domains || !domains.length || !hostname) return null;
  
  // First try exact match
  const exactMatch = domains.find(d => d.domainName === hostname);
  if (exactMatch) return exactMatch;

  // Then try includes match
  return domains.find(d => 
    hostname.includes(d.domainName) || 
    d.domainName.includes(hostname)
  );
};

const CompanyHeadDashboard = () => {
  const { logout, user, currentDomain, activeDomainConfig, setActiveDomainConfig } = useContext(AuthContext);
  const [dashboardAccess, setDashboardAccess] = useState(() => user?.user?.company?.companyHeadDashboardAccess || ['dashboard']);
  const [loading, setLoading] = useState(false);
  const [matchingCompany, setMatchingCompany] = useState(null);
  const [companyFetched, setCompanyFetched] = useState(false);
  
  // Fetch company data if company head has only company ID
  useEffect(() => {
    const fetchCompanyData = async () => {
      // Login may already provide the full company (with domains). Derive access
      // directly instead of relying on a page reload.
      const existingCompany = user?.user?.company;
      if (user?.role === 'CompanyHead' && existingCompany && typeof existingCompany === 'object'
          && Array.isArray(existingCompany.domains) && existingCompany.domains.length > 0) {
        const matchingDomain = findMatchingDomainConfig(existingCompany.domains, currentDomain)
          || existingCompany.domains[0];
        if (matchingDomain) {
          setMatchingCompany(existingCompany);
          setDashboardAccess(matchingDomain.companyHeadDashboardAccess || ['dashboard']);
          if (setActiveDomainConfig) setActiveDomainConfig(matchingDomain);
        }
        setCompanyFetched(true);
        return;
      }

      // Check if user exists, is CompanyHead role, and either has no company or just an ID
      if (user?.role === 'CompanyHead' && 
          user.user.company && 
          (typeof user.user.company === 'string' || !user.user.company.domains)) {
        
        setLoading(true);
        try {
          // Create secure API instance
          const api = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
            timeout: 10000,
            withCredentials: true,
          });
          
          // Add token to headers
          const token = localStorage.getItem('token');
          if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
          
          // Get company ID
          const companyId = typeof user.user.company === 'string' ? 
                          user.user.company : 
                          user.user.company._id;
          
          // Fetch the company
          const response = await api.get(`/companies/${companyId}`);
          const companyData = response.data;
          
          // Find matching domain configuration
          if (companyData && companyData.domains) {
            const matchingDomain = findMatchingDomainConfig(companyData.domains, currentDomain);
            if (matchingDomain) {
              setMatchingCompany(companyData);
              setDashboardAccess(matchingDomain.companyHeadDashboardAccess || ['dashboard']);
              
              // Update activeDomainConfig in context
              if (setActiveDomainConfig) {
                setActiveDomainConfig(matchingDomain);
              }
            } else {
              // If no matching domain, fetch all companies to find one that matches
              const allCompaniesResponse = await api.get('/companies');
              const companies = allCompaniesResponse.data;
              
              // Find a company with a matching domain
              const company = companies.find(company => {
                if (!company.domains) return false;
                return findMatchingDomainConfig(company.domains, currentDomain) !== null;
              });
              
              if (company) {
                // Get matching domain configuration from company
                const matchingDomain = findMatchingDomainConfig(company.domains, currentDomain);
                if (matchingDomain) {
                  setMatchingCompany(company);
                  setDashboardAccess(matchingDomain.companyHeadDashboardAccess || ['dashboard']);
                  
                  // Update activeDomainConfig in context
                  if (setActiveDomainConfig) {
                    setActiveDomainConfig(matchingDomain);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching company data:', error);
        } finally {
          setLoading(false);
          setCompanyFetched(true);
        }
      }
    };
    
    fetchCompanyData();
  }, [user, currentDomain, setActiveDomainConfig]);
  
  // Check if there's a domain match
  const isDomainMatched = useMemo(() => {
    // If we already found a matching company through fetch
    if (matchingCompany) return true;
    
    // Otherwise check if user's company has matching domain
    if (!user?.user?.company?.domains || !currentDomain) return false;
    return findMatchingDomainConfig(user.user.company.domains, currentDomain) !== null;
  }, [user, currentDomain, matchingCompany]);
  
  const getFilteredLinks = () => {
    // Always include dashboard
    const allLinks = [
      { 
        to: "/company-head/dashboard", 
        key: "dashboard", 
        icon: FaHome, 
        label: "Dashboard", 
        component: <Dashboard /> 
      }
    ];

    // Get access array from active domain config or fallback
    let accessArray = ['dashboard']; // Default, always show dashboard
    
    if (activeDomainConfig?.companyHeadDashboardAccess) {
      accessArray = activeDomainConfig.companyHeadDashboardAccess;
    } else if (matchingCompany?.domains) {
      const matchingDomain = findMatchingDomainConfig(matchingCompany.domains, currentDomain);
      if (matchingDomain?.companyHeadDashboardAccess) {
        accessArray = matchingDomain.companyHeadDashboardAccess;
      }
    } else if (user?.user?.company?.companyHeadDashboardAccess) {
      accessArray = user.user.company.companyHeadDashboardAccess;
    } else {
      accessArray = dashboardAccess || ['dashboard'];
    }

    // Add links based on permitted features
    accessArray.forEach(feature => {
      if (feature === 'dashboard') return; // Skip dashboard as it's already added

      // Map features to components
      let component;
      switch (feature) {
        case 'employee-management':
          component = <EmployeesManagement />;
          break;
        case 'brand-management':
          component = <BrandManagement />;
          break;
        case 'role-management':
          component = <RoleManagement />;
          break;
        case 'app-slider-management':
          component = <AppSliderManagement />;
          break;
        case 'app-access-log':
          component = <AppAccessLog />;
          break;
        case 'showroom-analytics':
          component = <ShowroomAnalytics />;
          break;
        case 'order-management':
          component = <OrderManagement />;
          break;
        case 'app-dealer-management':
          component = <AppTilesDealerManagement />;
          break;
        case 'app-dealer-access-logs':
          component = <AppTilesDealerAccessLog />;
          break;
        case 'app-dealer-interest-reports':
          component = <DealerInterestLog />;
          break;
        case 'tiles-management':
          component = <TilesManagement />;
          break;
        case 'stock-items-management':
          component = <StockItemsManagement />;
          break;
        case 'tally-management':
          component = <TallyManagement />;
          break;
        case 'tiles-day-book':
          component = <TilesDayBook />;
          break;
        case 'tiles-ledger':
          component = <TilesLedger />;
          break;
        case 'delivery-orders-reports':
          component = <DeliveryOrdersReports />;
          break;
        case 'activity-logs-reports':
          component = <ActivityLogsReports />;
          break;
        case 'choice-entry-reports':
          component = <ChoiceEntryReports />;
          break;
        case 'quotations-reports':
          component = <QuotationsReports />;
          break;
        case 'proforma-invoice-reports':
          component = <ProformaInvoiceReports />;
          break;
        case 'delivery-orders-detailed-reports':
          component = <DeliveryOrdersDetailedReports />;
          break;
        case 'goods-return-reports':
          component = <GoodsReturnReports />;
          break;
        case 'tally-reports':
          component = <TallyReports />;
          break;
        case 'whatsapp-automations':
          component = <WhatsAppAutomations />;
          break;
        default:
          component = <div>{feature.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ')}</div>;
      }

      allLinks.push({
        to: `/company-head/${feature}`,
        key: feature,
        icon: featureIcons[feature] || FaBox,
        label: feature.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' '),
        component: component
      });
    });

    return allLinks;
  };
  
  const sidebarLinks = getFilteredLinks();
  const currentPath = window.location.pathname;
  const matchingLink = sidebarLinks.find(link => link.to === currentPath);

  // Show warning banner for unmatched domains
  const domainWarningBanner = !isDomainMatched && companyFetched && (
    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
      <p className="font-bold">Domain Access Warning</p>
      <p>You don't have access to domain: <strong>{currentDomain}</strong></p>
      <p>Contact your administrator to request access to this domain.</p>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <DashboardLayout 
        title="Company Head Dashboard"
        sidebarLinks={[{ to: "/company-head/dashboard", key: "dashboard", icon: FaHome, label: "Dashboard" }]}
        onLogout={() => { logout(); window.location.href = "/"; }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600 mb-4" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-gray-600">Loading dashboard configuration...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Domain info display
  const domainInfo = activeDomainConfig ? 
    activeDomainConfig.domainName : 
    (matchingCompany ? 
      findMatchingDomainConfig(matchingCompany.domains, currentDomain)?.domainName : 
      currentDomain);

  const companyName = matchingCompany ? matchingCompany.name : user?.user?.company?.name || "Company";

  if (domainInfo) {
    document.title = `Company Head - ${companyName}`;
  }

  // If no matching domain and fetched, show access denied
  if (!isDomainMatched && companyFetched) {
    return (
      <DashboardLayout 
        title={`Company Head - ${companyName}`}
        sidebarLinks={[{ to: "/company-head/dashboard", key: "dashboard", icon: FaHome, label: "Dashboard" }]}
        onLogout={() => { logout(); window.location.href = "/"; }}
      >
        <div className="p-8 text-center">
          <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-red-700 mb-4">Domain Access Denied</h2>
            <p className="text-red-600 mb-2">
              You don't have access to domain: <strong>{currentDomain}</strong>
            </p>
            <p className="text-red-600">
              Please contact your administrator to request access.
            </p>
            <button
              onClick={logout}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title={`Company Head - ${companyName}`}
      sidebarLinks={sidebarLinks}
      onLogout={() => {
        logout();
        window.location.href = "/";
      }}
    >
      {domainWarningBanner}
      
      {matchingLink ? (
        matchingLink.component
      ) : (
        <div className="p-8 text-center">
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h2 className="text-xl font-semibold text-yellow-700 mb-2">Access Restricted</h2>
            <p className="text-yellow-600">
              You don't have access to this section for domain{" "}
              <strong>{currentDomain}</strong>.
            </p>
            <p className="mt-4 text-yellow-600">
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CompanyHeadDashboard;
